/**
 * How my sites are doing: how full each one has been and what it earned,
 * over the last month, quarter or year.
 */
import { useEffect, useState } from 'react';
import { api, ApiError } from '../lib/api';
import { useT } from '../lib/i18n';
import { Card, EmptyState, ErrorNote, Spinner, SubHeader } from '../components/Primitives';

interface Report {
  days: number;
  sites: {
    listingId: string;
    site: string;
    city: string;
    occupancyPct: number;
    bookings: number;
    earnedInr: number;
  }[];
  totals: { earnedInr: number; bookings: number; occupancyPct: number };
}

const inr = (n: number) => `₹${n.toLocaleString('en-IN')}`;

export function ReportsPage() {
  const T = useT();
  const [days, setDays] = useState(90);
  const [data, setData] = useState<Report | null>(null);
  const [error, setError] = useState<string | null>(null);
  const load = (d: number) => {
    setData(null);
    api
      .get<Report>(`/v1/partner/reports?days=${d}`)
      .then(setData, (e: unknown) => setError(e instanceof ApiError ? e.message : 'Error'));
  };
  useEffect(() => load(days), [days]);
  if (error) return <ErrorNote message={error} onRetry={() => load(days)} />;
  return (
    <div className="space-y-4">
      <SubHeader
        icon="chart"
        title={T('रिपोर्ट', 'Reports')}
        sub={T('हर साइट की कमाई और भराव', 'Earnings and occupancy, site by site')}
      />
      <div className="flex gap-1 rounded-2xl bg-panel p-1 shadow-card" role="tablist">
        {[30, 90, 365].map((d) => (
          <button
            key={d}
            role="tab"
            aria-selected={days === d}
            onClick={() => setDays(d)}
            className={`flex-1 rounded-xl px-3 text-sm font-semibold transition ${days === d ? 'text-white' : 'text-muted'}`}
            style={
              days === d
                ? { backgroundImage: 'linear-gradient(135deg, var(--sec-from), var(--sec-to))' }
                : undefined
            }
          >
            {d === 30
              ? T('30 दिन', '30 days')
              : d === 90
                ? T('3 महीने', '3 months')
                : T('1 साल', '1 year')}
          </button>
        ))}
      </div>
      {!data ? (
        <Spinner />
      ) : data.sites.length === 0 ? (
        <EmptyState
          title={T('अभी कोई साइट नहीं', 'No sites yet')}
          body={T(
            'साइट जोड़ें, फिर यहाँ उनका हिसाब दिखेगा।',
            'Add sites and their numbers show here.',
          )}
        />
      ) : (
        <>
          <div className="grid grid-cols-3 gap-2">
            <Stat label={T('कमाई', 'Earned')} value={inr(data.totals.earnedInr)} />
            <Stat label={T('बुकिंग', 'Bookings')} value={String(data.totals.bookings)} />
            <Stat label={T('भरी रही', 'Occupied')} value={`${data.totals.occupancyPct}%`} />
          </div>
          <ul className="space-y-2">
            {data.sites.map((s) => (
              <li key={s.listingId}>
                <Card>
                  <div className="flex items-baseline justify-between gap-2">
                    <p className="font-medium">{s.site}</p>
                    <span className="tnum text-sm font-semibold">{inr(s.earnedInr)}</span>
                  </div>
                  <p className="text-xs text-muted">
                    {s.city} · {s.bookings} {T('बुकिंग', 'bookings')}
                  </p>
                  <div className="mt-2 flex items-center gap-2">
                    <div className="h-2 flex-1 overflow-hidden rounded-full bg-panel-2">
                      <div
                        className="h-full rounded-full bg-brand-light"
                        style={{ width: `${s.occupancyPct}%` }}
                      />
                    </div>
                    <span className="tnum w-10 text-right text-xs text-muted">
                      {s.occupancyPct}%
                    </span>
                  </div>
                </Card>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <Card className="!p-3 text-center">
      <p className="tnum font-display text-lg font-semibold">{value}</p>
      <p className="text-xs text-muted">{label}</p>
    </Card>
  );
}
